import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Loader2, CheckCircle2, ArrowUpRight, Star, Gift, Clock, Users, Car, Trophy, Crown, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e91af2a1-1cd1-4d88-8ba8-233bf50929e1/images/3d291f0ac8fc03108e7602c3dc8042d4550191dee7b379d65ac6a66e2be7d20e.png";

const STATS = [
  ["$0", "Security Deposit"],
  ["2x", "Points Per Dollar"],
  ["FREE", "Membership, Always"],
  ["24/7", "Local STL Support"],
];

const MARQUEE = ["Birthday Bonus", "Rideshare Perks", "Free Metro Delivery", "No Deposit", "Free Rental Days", "Vehicle Upgrades", "Referral Bonuses", "Member-Only Specials"];

const EARN = [
  { icon: Car, title: "Rent With Us", pts: "2 pts / $1", desc: "Every dollar you spend on any rental earns double points. Weekly renters stack fast." },
  { icon: Clock, title: "Return On Time", pts: "+250 pts", desc: "Bring the car back on schedule and get rewarded for it — every single rental." },
  { icon: Users, title: "Refer A Friend", pts: "+500 pts", desc: "Your friend gets $15 off their first rental — you earn 500 points when it completes." },
  { icon: Star, title: "Leave A Review", pts: "+500 pts", desc: "Share your experience on Google or Facebook and pocket the points." },
];

const TIERS = [
  { name: "Member", pts: "0+", icon: Car, perks: ["2x points per dollar", "Birthday bonus points", "Member-only specials"] },
  { name: "Silver Driver", pts: "5,000+", icon: Zap, perks: ["Everything in Member", "Priority vehicle holds", "Free metro delivery"] },
  { name: "Gold Driver", pts: "15,000+", icon: Trophy, perks: ["Everything in Silver", "Free vehicle upgrades", "Flexible pickup times"] },
  { name: "Black Card", pts: "30,000+", icon: Crown, perks: ["Everything in Gold", "First access to new fleet", "VIP direct line to management"] },
];

const REWARDS = [
  { title: "Free Rental Day", pts: "15,000 pts", desc: "A full rental day on us — stack them for a free weekend." },
  { title: "Vehicle Upgrade", pts: "4,000 pts", desc: "Step up a class on your next rental at no charge." },
  { title: "$25 Rental Credit", pts: "2,500 pts", desc: "Knock $25 off any upcoming rental." },
  { title: "Free Metro Delivery", pts: "1,000 pts", desc: "We bring the car to you, anywhere in the STL metro." },
];

const gold = "#F2B705";

export default function DriversClub() {
  const [f, setF] = useState({ name: "", phone: "", email: "", rideshare: false, dob: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [memberId, setMemberId] = useState("");
  const joinRef = useRef(null);
  const scrollToJoin = () => joinRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.email.trim()) { toast.error("Please fill in your name, phone and email."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/drivers-club/join`, f);
      setMemberId(data.member_id || "");
      setDone(true);
      if (data.already_member) toast.success("You're already a member — welcome back!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — call us at (314) 936-1837.");
    } finally { setBusy(false); }
  };

  const navLink = "hidden md:inline text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-white transition-colors";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="drivers-club-page">
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <span className="font-display font-extrabold tracking-wide">AUTO-HIRE <span style={{ color: gold }}>STL</span></span>
          <nav className="flex items-center gap-7">
            <a href="#how" className={navLink}>How It Works</a>
            <a href="#tiers" className={navLink}>Tiers</a>
            <a href="#earn" className={navLink}>Earn</a>
            <a href="#rewards" className={navLink}>Rewards</a>
            <a href="/club/dashboard" data-testid="club-nav-login" className={navLink}>Member Login</a>
            <button data-testid="club-nav-join" onClick={scrollToJoin} className="bg-[#F2B705] text-black text-[11px] font-extrabold tracking-[0.2em] uppercase px-6 py-2.5 rounded-full hover:bg-white transition-colors">Join Free</button>
          </nav>
        </div>
      </header>

      {done ? (
        <section className="max-w-xl mx-auto px-5 py-24 text-center space-y-6" data-testid="club-confirmation">
          <div className="text-6xl">🏁</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold">You&apos;re in the Club.</h1>
          <p className="text-white/60">Welcome to the Drivers Club, {f.name.split(" ")[0]}. We&apos;ll text you your member perks shortly — your points start counting on your very next rental.</p>
          {memberId && (
            <a data-testid="club-view-dashboard" href={`/club/dashboard?m=${memberId}`}
              className="inline-flex items-center gap-2 bg-[#F2B705] text-black text-xs font-extrabold tracking-[0.2em] uppercase px-9 py-4 rounded-full hover:bg-white transition-colors">
              View My Member Dashboard <ArrowUpRight size={15} />
            </a>
          )}
          <div>
          <a href="tel:+13149361837" className="inline-flex items-center gap-2 font-bold" style={{ color: gold }}><Phone size={15} /> (314) 936-1837</a>
          </div>
        </section>
      ) : (
        <>
          {/* HERO */}
          <section className="relative overflow-hidden">
            <img src={HERO_IMG} alt="St. Louis skyline" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#050505]/60 to-[#050505]" />
            <div className="relative max-w-6xl mx-auto px-5 pt-20 pb-16 sm:pt-28 sm:pb-24">
              <h1 className="font-display font-black leading-[0.95] text-6xl sm:text-7xl lg:text-8xl uppercase">
                Join The<br />Drivers <span style={{ color: gold }}>Club.</span>
              </h1>
              <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
                Earn points on every rental. Redeem for free rental days, exclusive discounts, birthday rewards and referral bonuses. Membership is always free.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button data-testid="club-hero-join" onClick={scrollToJoin} className="inline-flex items-center gap-2 bg-[#F2B705] text-black text-xs font-extrabold tracking-[0.2em] uppercase px-9 py-4 rounded-full hover:bg-white transition-colors">
                  Join Free <ArrowUpRight size={15} />
                </button>
                <a href="tel:+13149361837" className="inline-flex items-center gap-2 border border-white/25 text-xs tracking-[0.2em] uppercase px-7 py-4 rounded-full text-white/80 hover:border-white transition-colors">
                  <Phone size={13} /> (314) 936-1837
                </a>
              </div>
            </div>
            {/* STATS */}
            <div className="relative border-t border-white/10 bg-[#050505]/80 backdrop-blur">
              <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4">
                {STATS.map(([v, l], i) => (
                  <div key={l} className={`py-7 px-4 ${i > 0 ? "md:border-l md:border-white/10" : ""}`}>
                    <p className="font-display font-black text-4xl">{v}</p>
                    <p className="text-white/40 text-[10px] tracking-[0.25em] uppercase mt-1">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* MARQUEE */}
          <div className="border-y border-white/10 bg-black overflow-hidden py-4">
            <div className="club-marquee whitespace-nowrap flex gap-10">
              {[...MARQUEE, ...MARQUEE].map((m, i) => (
                <span key={i} className="text-[11px] tracking-[0.35em] uppercase text-white/60 flex items-center gap-10">
                  {m} <span style={{ color: gold }}>✦</span>
                </span>
              ))}
            </div>
          </div>

          {/* EARN */}
          <section id="earn" className="max-w-6xl mx-auto px-5 py-20 scroll-mt-20">
            <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: gold }}>How It Works</p>
            <h2 id="how" className="font-display font-black text-4xl sm:text-5xl uppercase mt-2 scroll-mt-28">Points Stack Up Fast.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {EARN.map(({ icon: Icon, title, pts, desc }) => (
                <div key={title} className="border border-white/10 bg-white/[0.02] rounded-xl p-6 hover:border-[#F2B705]/50 transition-colors">
                  <Icon size={22} style={{ color: gold }} />
                  <p className="font-display font-bold text-lg mt-4">{title}</p>
                  <p className="font-black text-2xl mt-1" style={{ color: gold }}>{pts}</p>
                  <p className="text-white/50 text-sm mt-2 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* TIERS */}
          <section id="tiers" className="bg-[#0A0A0A] border-y border-white/10 scroll-mt-20">
            <div className="max-w-6xl mx-auto px-5 py-20">
              <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: gold }}>Member Tiers</p>
              <h2 className="font-display font-black text-4xl sm:text-5xl uppercase mt-2">The More You Ride,<br />The More You Rise.</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                {TIERS.map(({ name, pts, icon: Icon, perks }, i) => (
                  <div key={name} className={`rounded-xl p-6 border ${i === 3 ? "border-[#F2B705] bg-[#F2B705]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
                    <div className="flex items-center justify-between">
                      <Icon size={20} style={{ color: gold }} />
                      <span className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{pts} pts</span>
                    </div>
                    <p className="font-display font-extrabold text-xl mt-3">{name}</p>
                    <ul className="mt-3 space-y-1.5">
                      {perks.map((p) => <li key={p} className="text-white/55 text-xs flex gap-2"><span style={{ color: gold }}>✦</span>{p}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* REWARDS */}
          <section id="rewards" className="max-w-6xl mx-auto px-5 py-20 scroll-mt-20">
            <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: gold }}>Redeem</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase mt-2">Turn Miles Into Free Rides.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
              {REWARDS.map(({ title, pts, desc }) => (
                <div key={title} className="flex items-center justify-between gap-4 border border-white/10 bg-white/[0.02] rounded-xl p-6 hover:border-[#F2B705]/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <Gift size={22} style={{ color: gold }} className="shrink-0" />
                    <div>
                      <p className="font-display font-bold text-lg">{title}</p>
                      <p className="text-white/50 text-sm">{desc}</p>
                    </div>
                  </div>
                  <span className="font-black whitespace-nowrap" style={{ color: gold }}>{pts}</span>
                </div>
              ))}
            </div>
          </section>

          {/* JOIN FORM */}
          <section ref={joinRef} className="border-t border-white/10 bg-[#0A0A0A] scroll-mt-16">
            <div className="max-w-lg mx-auto px-5 py-20">
              <h2 className="font-display font-black text-4xl uppercase text-center">Join Free.<br /><span style={{ color: gold }}>Takes 30 Seconds.</span></h2>
              <form onSubmit={submit} className="mt-8 space-y-4">
                {[["name", "Full Name", "text", "club-form-name"], ["phone", "Phone", "tel", "club-form-phone"], ["email", "Email", "email", "club-form-email"]].map(([k, label, type, tid]) => (
                  <label key={k} className="block text-[11px] text-white/50 uppercase tracking-[0.2em] font-semibold">{label} *
                    <input data-testid={tid} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)}
                      className="mt-1.5 w-full bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded-lg outline-none px-4 py-3.5 text-white text-base transition-colors" />
                  </label>
                ))}
                <label className="block text-[11px] text-white/50 uppercase tracking-[0.2em] font-semibold">Date of Birth <span className="normal-case tracking-normal text-white/35">(optional — unlocks your 2,500-point birthday reward)</span>
                  <input data-testid="club-form-dob" type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)}
                    className="mt-1.5 w-full bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded-lg outline-none px-4 py-3.5 text-white text-base transition-colors [color-scheme:dark]" />
                </label>
                <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
                  <input data-testid="club-form-rideshare" type="checkbox" checked={f.rideshare} onChange={(e) => set("rideshare", e.target.checked)} className="w-4 h-4 accent-[#F2B705]" />
                  I drive rideshare / gig (unlock Rideshare Perks)
                </label>
                <button data-testid="club-form-submit" type="submit" disabled={busy}
                  className="w-full py-4 rounded-full bg-[#F2B705] text-black font-extrabold text-sm tracking-[0.2em] uppercase disabled:opacity-60 flex items-center justify-center gap-2 hover:bg-white transition-colors">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Joining…</> : <><CheckCircle2 size={16} /> Join The Drivers Club</>}
                </button>
                <p className="text-white/30 text-[11px] text-center leading-relaxed">Free forever. No card required. We may contact you by call, text or email about your membership. No spam — ever.</p>
              </form>
            </div>
          </section>
        </>
      )}

      <footer className="border-t border-white/10 py-8 text-center text-white/30 text-xs">
        Drivers Club by Auto-Hire STL · (314) 936-1837 · St. Louis, MO
      </footer>

      <style>{`
        .club-marquee { animation: club-scroll 28s linear infinite; }
        @keyframes club-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
}
